import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Entete } from '../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Etiquette,
  Nombre,
  Vide,
  formaterDateHeure,
} from '../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../composants/ui/index.ts';
import { useApercuPdf } from '../composants/apercu-pdf.tsx';

/**
 * Inventaires — RG-055, RG-056.
 *
 * Le comptage est saisi par le vendeur et n'a AUCUN effet sur le stock. Seule
 * la validation depuis cet écran crée les mouvements d'ajustement — et celui
 * qui a compté ne peut pas valider son propre comptage.
 */

interface Comptage {
  id: string;
  numero: string;
  statut: string;
  perimetre: string | null;
  compte_le: string;
  compte_par: string;
  boutique: string;
  lignes: number;
  lignes_en_ecart: number;
}

interface Ligne {
  produit_id: string;
  designation: string;
  quantite_theorique: number;
  quantite_comptee: number;
  ecart: number;
  motif: string | null;
}

export function Inventaires(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId, peut } = useSession();
  const client = useQueryClient();

  // En vue consolidee, chaque ligne doit dire de quelle boutique elle
  // vient : sans cette colonne, la liste melange sans le dire.
  const consolide = boutiqueId === null;
  const [ouvert, setOuvert] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ['inventaires', boutiqueId],
    queryFn: () =>
      appeler<{ donnees: Comptage[] }>(
        `/inventaires${boutiqueId === null ? '' : `?boutique_id=${boutiqueId}`}`,
      ),
    select: (reponse) => reponse.donnees,
  });

  return (
    <>
      <Entete
        titre="Inventaires"
        sous="Un comptage ne modifie le stock qu’une fois validé."
      />

      <Erreur erreur={error} />

      <div className="pile">
        {ouvert !== null && (
          <Detail
            inventaireId={ouvert}
            validable={peut('inventaire.valider')}
            onFerme={() => {
              setOuvert(null);
            }}
            onTranche={() => {
              setOuvert(null);
              void client.invalidateQueries({ queryKey: ['inventaires'] });
            }}
          />
        )}

        <Carte>
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucun comptage enregistré." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Inventaires',
                        nomFichier: 'inventaires',
                        colonnes: [
                          { cle: 'numero', entete: 'Numéro', rendu: () => null, texte: (c: Comptage) => c.numero },
                          ...(consolide
                            ? [{ cle: 'boutique', entete: 'Boutique', rendu: () => null, texte: (c: Comptage) => c.boutique }]
                            : []),
                          {
                            cle: 'compte_le',
                            entete: 'Compté le',
                            rendu: () => null,
                            texte: (c: Comptage) => formaterDateHeure(c.compte_le),
                          },
                          { cle: 'compte_par', entete: 'Par', rendu: () => null, texte: (c: Comptage) => c.compte_par },
                          {
                            cle: 'lignes',
                            entete: 'Lignes',
                            numerique: true,
                            rendu: () => null,
                            texte: (c: Comptage) => String(c.lignes),
                          },
                          {
                            cle: 'ecart',
                            entete: 'En écart',
                            numerique: true,
                            rendu: () => null,
                            texte: (c: Comptage) => String(c.lignes_en_ecart),
                          },
                          {
                            cle: 'statut',
                            entete: 'Statut',
                            rendu: () => null,
                            texte: (c: Comptage) =>
                              c.statut === 'EN_ATTENTE_VALIDATION'
                                ? 'À valider'
                                : c.statut === 'VALIDE'
                                  ? 'Validé'
                                  : c.statut,
                          },
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Numéro</th>
                    {consolide && <th>Boutique</th>}
                    <th>Compté le</th>
                    <th>Par</th>
                    <th>Lignes</th>
                    <th>En écart</th>
                    <th>Statut</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((comptage) => (
                    <tr key={comptage.id}>
                      <td className="mono">{comptage.numero}</td>
                      {consolide && <td className="mono">{comptage.boutique}</td>}
                      <td>
                        <DateHeure valeur={comptage.compte_le} />
                      </td>
                      <td>{comptage.compte_par}</td>
                      <td>
                        <Nombre valeur={comptage.lignes} />
                      </td>
                      <td>
                        {comptage.lignes_en_ecart > 0 ? (
                          <Etiquette
                            ton="ambre"
                            texte={String(comptage.lignes_en_ecart)}
                          />
                        ) : (
                          <Nombre valeur={0} />
                        )}
                      </td>
                      <td>
                        {comptage.statut === 'EN_ATTENTE_VALIDATION' ? (
                          <Etiquette ton="ambre" texte="à valider" />
                        ) : comptage.statut === 'VALIDE' ? (
                          <Etiquette ton="vert" texte="validé" />
                        ) : (
                          <Etiquette ton="rouge" texte={comptage.statut.toLowerCase()} />
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="bouton--discret"
                          onClick={() => {
                            setOuvert(comptage.id);
                          }}
                        >
                          Examiner
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}

function Detail({
  inventaireId,
  validable,
  onFerme,
  onTranche,
}: {
  inventaireId: string;
  validable: boolean;
  onFerme: () => void;
  onTranche: () => void;
}): ReactNode {
  const [motif, setMotif] = useState('');

  const { data, isPending } = useQuery({
    queryKey: ['inventaire', inventaireId],
    queryFn: () =>
      appeler<{ numero: string; statut: string; lignes: Ligne[] }>(
        `/inventaires/${inventaireId}`,
      ),
  });

  const decision = useMutation({
    mutationFn: (choix: 'VALIDE' | 'REJETE') =>
      appeler(`/inventaires/${inventaireId}/validation`, {
        methode: 'POST',
        cleIdempotence: cleNeuve(),
        corps: { decision: choix, motif: motif.trim() === '' ? null : motif.trim() },
      }),
    onSuccess: onTranche,
  });

  const enAttente = data?.statut === 'EN_ATTENTE_VALIDATION';

  return (
    <Carte
      titre={`Comptage ${data?.numero ?? ''}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      {isPending ? (
        <Chargement />
      ) : (
        <>
          <div className="tableau-defilant">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Théorique</th>
                  <th>Compté</th>
                  <th>Écart</th>
                  <th>Motif du vendeur</th>
                </tr>
              </thead>
              <tbody>
                {(data?.lignes ?? []).map((ligne) => (
                  <tr key={ligne.produit_id}>
                    <td>{ligne.designation}</td>
                    <td>
                      <Nombre valeur={ligne.quantite_theorique} />
                    </td>
                    <td>
                      <Nombre valeur={ligne.quantite_comptee} />
                    </td>
                    <td>
                      {ligne.ecart === 0 ? (
                        <Nombre valeur={0} />
                      ) : (
                        <Etiquette
                          ton={ligne.ecart < 0 ? 'rouge' : 'ambre'}
                          texte={ligne.ecart > 0 ? `+${String(ligne.ecart)}` : String(ligne.ecart)}
                        />
                      )}
                    </td>
                    <td>{ligne.motif ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {validable && enAttente && (
            <>
              <div className="champ" style={{ marginTop: 16 }}>
                <label htmlFor="motif-decision">
                  Motif — obligatoire pour rejeter, 10 caractères au minimum
                </label>
                <textarea
                  id="motif-decision"
                  name="motif"
                  rows={2}
                  value={motif}
                  onChange={(evenement) => {
                    setMotif(evenement.target.value);
                  }}
                />
              </div>

              <Erreur erreur={decision.error} />

              <div className="ligne">
                <button
                  type="button"
                  disabled={decision.isPending}
                  onClick={() => {
                    decision.mutate('VALIDE');
                  }}
                >
                  Valider les écarts
                </button>
                <button
                  type="button"
                  className="bouton--discret"
                  disabled={decision.isPending || motif.trim().length < 10}
                  onClick={() => {
                    decision.mutate('REJETE');
                  }}
                >
                  Rejeter le comptage
                </button>
              </div>
            </>
          )}
        </>
      )}
    </Carte>
  );
}
